import { CrudOptions } from '@hutsix/ngxh6';
import cruds_conf from '../_generated/ext_config/cruds.json';
import { permitIssuedDate } from '../modules/_shared/components/permit/permit-status-badge/permit-issued-date';
import { permitStatusPill } from '../modules/_shared/components/permit/permit-status-badge/permit-status';
import { permitIssuedBy } from '../modules/_shared/components/permit/permit-status-badge/permit-issued-by';
import { optionalDate } from '../modules/_shared/components/permit/permit-status-badge/optional-date';
import { optionalText } from '../modules/_shared/components/permit/permit-status-badge/optional-text';
import { permitNumberCell } from '../modules/_shared/components/permit/permit-status-badge/permit-number-cell';
import { money } from '../modules/_shared/components/permit/permit-status-badge/money';
import { capacityLabel } from '../modules/_shared/components/permit/permit-status-badge/capacity-label';
import { businessStatusPill } from '../modules/_shared/components/permit/permit-status-badge/business-status';
import { pageTypeBadge } from './page-type-badge';

/** Override generated CRUD values here */
const cruds_conf_overrides: Override = {
  // Information-hub pages: a readable table of what exists and whether it's live.
  pages: {
    tableSpec: {
      columns: [
        { header: 'Title', value: 'title', class: 'w-50' },
        // Info pages (seeded, fixed address, can't be deleted) vs TLC's own pages.
        { header: 'Type', value: 'system', type: 'customFunction', typeOption: pageTypeBadge },
        { header: 'Web address', value: 'slug' },
        { header: 'Published', type: 'boolean', value: 'published' },
        { header: 'In menu', type: 'boolean', value: 'showInMenu' },
        { header: 'Order', value: 'sortOrder' },
      ],
    },
  },
  locations: {
    tableSpec: {
      columns: [
        {
          header: 'Name',
          value: 'name',
          class: 'w-75',
        },
        {
          header: 'Requires manual validation',
          type: 'boolean',
          value: 'requiresManualValidation',
        },
        {
          header: 'Active',
          type: 'boolean',
          value: 'active',
        },
      ],
    },
  },
  // Payments. The row action was hidden and no actionUrl was set, so the order page
  // existed as a route that nothing in the UI could reach - which also stranded the
  // invoice, since that is where it opens from.
  orders: {
    tableSpec: {
      actionUrl: '/crm/orders/',
      actionLabel: 'View',
      columns: [
        {
          // `string` (the entity's __toString) is not serialised on the collection, so
          // this column had been rendering blank. `nbr` is the number itself.
          header: 'Order #',
          value: 'nbr',
          linkAction: true,
        },
        {
          header: 'Status',
          value: 'status',
        },
        {
          header: 'Date Completed',
          type: 'date',
          typeOption: 'short',
          value: 'dateCompleted',
        },
        {
          header: 'Total',
          type: 'currency',
          value: 'total',
        },
      ],
    },
  },
  // Clan areas: TLC-managed list for fishing/hunting selection. Per-area price
  // overrides live in the edit modal (blank = default), so the list stays to
  // name + active.
  // Businesses. A standard CRUD rather than the hand-rolled table it replaces, so it
  // gets search, paging and a status filter for free - and so it behaves like every
  // other list in the CRM. Editing still happens on the detail page, which does more
  // than persist fields: approving a business also creates its first login.
  businesses: {
    hideHeader: true,
    // Four tabs share this slug, so the message has to read on all of them. The
    // default is "No Business Logins", which on the Applications tab says the opposite
    // of what is true - there are business logins, just none waiting on a decision.
    emptyMessageOverride: 'Nothing in this view',
    tableSpec: {
      actionUrl: '/crm/businesses/',
      actionLabel: 'View',
      columns: [
        { header: 'Name', value: 'name', linkAction: true },
        { header: 'ABN', value: 'abn', type: 'customFunction', typeOption: optionalText },
        // Who TLC deal with. On the list rather than behind the row action because a
        // pending application is judged on who is asking as much as on the name.
        { header: 'Contact', value: 'contactDisplayName', type: 'customFunction', typeOption: optionalText },
        { header: 'Email', value: 'contactEmail', type: 'customFunction', typeOption: optionalText },
        { header: 'Tourism & Event', value: 'guestFeeLabel' },
        { header: 'People', value: 'employeeCount' },
        // `value` empty so the formatter gets the row - same reason as the permit
        // status column.
        { header: 'Status', value: '', type: 'customFunction', typeOption: businessStatusPill },
      ],
    },
  },

  // Config -> Blacklist. Reason is on the list rather than hidden behind the edit
  // modal: it is the field a staff member needs when a flagged application lands on
  // their screen, and a table of bare names is not something anyone can act on.
  blacklist_entries: {
    tableSpec: {
      columns: [
        { header: 'First name', value: 'firstName' },
        { header: 'Last name', value: 'lastName' },
        // Optional, like the date below it - the API omits the key entirely when it is
        // unset, so a plain column renders an empty cell that reads as missing data.
        { header: 'Email', value: 'email', type: 'customFunction', typeOption: optionalText },
        // Optional field, so a blank cell is the common case and reads as broken. The
        // `date` type renders nothing for an absent value - see the helper for why it
        // has to take unknown rather than a date string.
        { header: 'Date of birth', value: 'dateOfBirth', type: 'customFunction', typeOption: optionalDate },
        { header: 'Reason', value: 'reason' },
        { header: 'Active', value: 'active', type: 'boolean' },
      ],
    },
  },
  // Recreation areas: the sites themselves. feePerVehicle and boatAccess are
  // deliberately NOT shown - the fee is superseded by charges, and boat access was the
  // dead flag we removed from the form. "Charges" is the useful one: it says at a
  // glance which sites are on the new charge model.
  recreation_areas: {
    tableSpec: {
      columns: [
        { header: 'Name', value: 'name', class: 'w-25' },
        // Null capacity means unlimited, not zero - the formatter says so.
        { header: 'Vehicles', value: 'capacity', type: 'customFunction', typeOption: capacityLabel },
        { header: 'Max nights', value: 'maxNights' },
        { header: 'Day use only', value: 'dayUseOnly', type: 'boolean' },
        { header: 'Charges', value: 'chargeCount' },
        { header: 'Staff review', value: 'requiresManualValidation', type: 'boolean' },
        { header: 'Active', value: 'active', type: 'boolean' },
      ],
    },
  },
  // Recreation charges: the library of charges recreation sites apply. The two flags and the
  // default price are what tells one charge from another at a glance - "Camping, $100,
  // per vehicle per night" reads without opening it.
  charge_types: {
    tableSpec: {
      columns: [
        { header: 'Name', value: 'name', class: 'w-50' },
        // Optional: a charge whose price is always set per site has no default, and the
        // API omits the key - money() renders a dash rather than "$NaN".
        { header: 'Default amount', value: 'defaultAmount', type: 'customFunction', typeOption: money },
        { header: 'Per vehicle / boat', value: 'perVehicle', type: 'boolean' },
        { header: 'Per person', value: 'perPerson', type: 'boolean' },
        { header: 'Per night', value: 'perNight', type: 'boolean' },
        { header: 'Active', value: 'active', type: 'boolean' },
      ],
    },
  },
  clan_areas: {
    tableSpec: {
      columns: [
        {
          header: 'Name',
          value: 'name',
          class: 'w-75',
        },
        {
          header: 'Active',
          type: 'boolean',
          value: 'active',
        },
      ],
    },
  },
  // Fishing zones: the same shape as clan areas, and a separate list on purpose -
  // fishing is scoped by river system so clan boundaries stay off the water.
  fishing_zones: {
    tableSpec: {
      columns: [
        {
          header: 'Name',
          value: 'name',
          class: 'w-75',
        },
        {
          header: 'Active',
          type: 'boolean',
          value: 'active',
        },
      ],
    },
  },
  // Permit types: the seeded per-type base price + GST flag, edited from Config →
  // Permit types. #[Crud] supplies the endpoint/class; we just define the columns.
  // Create/delete are disabled on the form (#[Form] flags) - edit only.
  permit_type_settings: {
    tableSpec: {
      columns: [
        {
          header: 'Permit type',
          value: 'label',
          class: 'w-50',
        },
        {
          // Fishing/hunting show both per-clan-area terms (14-day · annual);
          // flat-priced types show a single price. From PermitTypeSettings::getPriceSummary().
          header: 'Price',
          value: 'priceSummary',
        },
        {
          header: 'GST applies',
          type: 'boolean',
          value: 'gstApplies',
        },
      ],
    },
  },
  // Permits: #[Crud] on the entity supplies the endpoint/class/filterable/searchable;
  // we only add the table columns + the "View" row action (→ detail page). hideHeader
  // because the page heading + status tabs already provide the header.
  permits: {
    hideHeader: true,
    tableSpec: {
      actionUrl: '/crm/permits/',
      actionLabel: 'View',
      columns: [
        // Permit # links to the permit only once it has a number (approved); before that
        // it's a plain dash, not a link (TLC, 30 Sep). The link is built in the formatter
        // rather than by linkAction, because linkAction would wrap the dash too and
        // underline it into a fake "=". `value:''` hands the formatter the whole row.
        // Un-numbered rows are opened with the row's own "View" button; the applicant name
        // is deliberately NOT a link - it looks like it would open the person, but every
        // link here goes to the permit, which reads as a bug.
        { header: 'Permit #', value: '', type: 'customFunction', typeOption: permitNumberCell, orderBy: 'permitNumber' },
        { header: 'Type', value: 'typeLabel' },
        { header: 'Applicant', value: 'applicantName' },
        { header: 'Email', value: 'applicantEmail' },
        // Every status tab excludes business-issued permits, so this reads "-" all the
        // way down on those - which was the argument for keeping it off `permits` and
        // giving All its own crud. That crud is gone: a hand-written copy of a
        // generated config drifts from it silently, and the drift broke the list in a
        // way that looked like paging. One always-dash column is a smaller cost.
        { header: 'Issued by', value: 'businessName', type: 'customFunction', typeOption: permitIssuedBy },
        { header: 'Start', value: 'accessStartDate', type: 'date', typeOption: 'short', orderBy: 'accessStartDate' },
        { header: 'End', value: 'accessEndDate', type: 'date', typeOption: 'short', orderBy: 'accessEndDate' },
        { header: 'Location', value: 'locationLabel' },
        // Date applied - the record's creation, set on every permit including those still
        // in a queue, so no dash treatment needed. Sits before Issued: the two lifecycle
        // dates read left to right, applied then issued. Sorts on `created` (what appliedAt
        // returns) - the API orders on the real property, not the display alias.
        { header: 'Applied', value: 'appliedAt', type: 'date', typeOption: 'short', orderBy: 'created' },
        // The list is sorted by this, so it needs to be visible - a sort you can't see
        // reads as no sort at all. A dash where there is no date yet, rather than an
        // empty cell that reads as missing data.
        { header: 'Issued', value: 'issuedAt', type: 'customFunction', typeOption: permitIssuedDate, orderBy: 'issuedAt' },
        // The same pill as the detail page, so the list is scannable by colour and a
        // status never looks like two different things in two places.
        //
        // `value` is EMPTY on purpose: that makes the CRUD hand the formatter the whole
        // row, which it needs because the pill depends on two fields now - the status
        // for its colour and `upcoming` for whether the permit is actually in force.
        // Still sortable: `orderBy` drives the header's sort independently of `value`.
        { header: 'Status', value: '', type: 'customFunction', typeOption: permitStatusPill, orderBy: 'status' },
      ],
    },
  },
};

/** Create fully custom CRUDs here (generated ones are overridden above). */
const cruds = {
  /**
   * The Permits → Business Logins tab.
   *
   * Same endpoint and same row action as `permits`. It keeps its own slug for two
   * reasons that still hold: its own query key, so filtering this tab does not leak
   * into the status tabs, and filterable/searchable off, because the tab renders the
   * business filter panel itself.
   *
   * The tab scopes the list to business-issued permits via queryExtras; this only
   * decides what the rows look like. Its "Issued by" column is the plain value - every
   * row here has a business, so there is no empty case to render.
   */
  /**
   * The Permits -> All tab.
   *
   * The only list that mixes both kinds of permit, which is why it is the only one
   * with an "Issued by" column carrying a dash: on the status tabs every row would be
   * a dash, and on the Business Logins tab every row would be a name. Here the column
   * is the difference between the two halves of the list.
   *
   * Same endpoint, columns and row action as `permits`; its own query key so filtering
   * All does not leak into the status tabs.
   */
  business_permits: {
    apiEndpoint: '/api/v1/permits',
    // Its own query key, so filtering this tab doesn't leak into the status tabs -
    // they share an endpoint but not a question.
    queryKey: 'bizpermit',
    apiClassName: 'App\\Entity\\Permit\\Permit',
    objectLabel: 'Permit',
    hideHeader: true,
    // The tab renders its own filter panel above the list (the business one), so the
    // crud must not render the entity's default filter as well - two panels stacked,
    // each setting the same query, is worse than either.
    filterable: false,
    searchable: false,
    tableSpec: {
      actionUrl: '/crm/permits/',
      actionLabel: 'View',
      columns: [
        { header: 'Permit #', value: '', type: 'customFunction', typeOption: permitNumberCell, orderBy: 'permitNumber' },
        { header: 'Issued by', value: 'businessName' },
        { header: 'Type', value: 'typeLabel' },
        { header: 'Applicant', value: 'applicantName' },
        { header: 'Email', value: 'applicantEmail' },
        { header: 'Start', value: 'accessStartDate', type: 'date', typeOption: 'short', orderBy: 'accessStartDate' },
        { header: 'End', value: 'accessEndDate', type: 'date', typeOption: 'short', orderBy: 'accessEndDate' },
        { header: 'Applied', value: 'appliedAt', type: 'date', typeOption: 'short', orderBy: 'created' },
        { header: 'Issued', value: 'issuedAt', type: 'customFunction', typeOption: permitIssuedDate, orderBy: 'issuedAt' },
        { header: 'Status', value: '', type: 'customFunction', typeOption: permitStatusPill, orderBy: 'status' },
      ],
    },
  },
};

/**
 *
 * You shouldn't need to change anything below
 *
 */
export function getCrudsConfig() {
  const c = JSON.parse(JSON.stringify(cruds_conf));
  for (const [k, v] of Object.entries(cruds_conf_overrides)) {
    if (c[k]) {
      c[k] = Object.assign(cruds_conf[k] || {}, v);
    } else {
      console.warn(`Attempt to override generated CRUD failed. Key '${k}' doesn't exist`);
    }
  }
  return { ...c, ...cruds };
}

export function getCrud(slug: CrudKeys): CrudOptions {
  return getCrudsConfig()[slug];
}

declare global {
  type CrudKeys = keyof typeof cruds | keyof typeof cruds_conf;
}

type Override = { [Property in keyof Partial<typeof cruds_conf>]: Partial<CrudOptions> };
